import {
  type GqlClaimFilter,
  type GqlIssuedClaim,
  NetworkName,
  NodeConnection,
  connect_to_node,
  fetch_claims,
} from '@coinweb/wallet-lib';
import { type Pagination } from 'dex-app.cm';

import { CONTRACT_PARAMS, Currency, ENV } from '../constants';

type IndexClaimKey = {
  first_part: unknown[];
  second_part: [number | string, ...unknown[]];
};

let cwebWalletNode: NodeConnection | null = null;
const getCwebWalletNode = () => {
  if (!cwebWalletNode) {
    cwebWalletNode = connect_to_node(import.meta.env.VITE_BACKEND_URL as string);
  }

  return cwebWalletNode;
};

export type ContractClient = {
  fetchClaims: <T>(
    firstPart: NonNullable<unknown>,
    secondPart: T,
    range?: {
      start: T extends null ? unknown : T | null;
      end: T extends null ? unknown : T | null;
    },
    pagination?: Pagination
  ) => Promise<GqlIssuedClaim[]>;
  updateClient: () => void;
  contractAddress: string;
  networkName: NetworkName;
};

export const createClient = (contractAddress: string, networkName: NetworkName) => {
  const createClaimFilter = <T extends NonNullable<unknown>>(
    firstPart: NonNullable<unknown>,
    secondPart: T | null = null,
    range?: {
      start: T;
      end: T;
    }
  ): GqlClaimFilter => {
    return {
      issuer: { FromSmartContract: contractAddress },
      keyFirstPart: firstPart,
      keySecondPart: secondPart,
      startsAtKeySecondPart: range?.start ?? null,
      endsAtKeySecondPart: range?.end ?? null,
    };
  };

  const fetchClaims = async <T extends NonNullable<unknown>>(
    firstPart: NonNullable<unknown>,
    secondPart: T | null = null,
    range?: {
      start: T;
      end: T;
    },
    pagination?: Pagination
  ): Promise<GqlIssuedClaim[]> => {
    if (!contractAddress || !networkName) {
      throw new Error('Network parameters are not defined');
    }

    const filter = createClaimFilter(firstPart, secondPart, range);

    const loadClaims = async () => {
      const rawData = await fetch_claims(getCwebWalletNode(), [filter], networkName, true);

      const sortedData = rawData.sort((a, b) => {
        let aValue: string | number | bigint = (a.content.key as IndexClaimKey).second_part[0];
        let bValue: string | number | bigint = (b.content.key as IndexClaimKey).second_part[0];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          if (aValue.startsWith('0x')) {
            aValue = BigInt(aValue);
          } else {
            aValue = BigInt(`0x${aValue}`);
          }

          if (bValue.startsWith('0x')) {
            bValue = BigInt(bValue);
          } else {
            bValue = BigInt(`0x${bValue}`);
          }
        }

        if (aValue < bValue) {
          return 1;
        } else if ((a.content.key as IndexClaimKey).second_part[0] > (b.content.key as IndexClaimKey).second_part[0]) {
          return -1;
        } else {
          return 0;
        }
      });

      if (pagination) {
        if (pagination.offset > 0 && pagination.limit >= 0) {
          return sortedData.slice(pagination.offset, pagination.offset + pagination.limit);
        }
      }

      return sortedData;
    };

    const claimsRequest = loadClaims();

    const result = await claimsRequest;

    return result;
  };

  const updateClient = () => {};

  return {
    fetchClaims,
    updateClient,
    contractAddress,
    networkName,
  } as ContractClient;
};

export const baseClients: {
  [key in Exclude<Currency, Currency.CWEB>]: ContractClient;
} = {
  [Currency.ETH]: createClient(
    CONTRACT_PARAMS[Currency.ETH].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.ETH].NETWORK_NAME
  ),
  [Currency.BNB]: createClient(
    CONTRACT_PARAMS[Currency.BNB].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.BNB].NETWORK_NAME
  ),
  [Currency.USDT_ETH]: createClient(
    CONTRACT_PARAMS[Currency.USDT_ETH].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDT_ETH].NETWORK_NAME
  ),
  [Currency.USDT_BNB]: createClient(
    CONTRACT_PARAMS[Currency.USDT_BNB].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDT_BNB].NETWORK_NAME
  ),
  [Currency.BTC]: createClient(
    CONTRACT_PARAMS[Currency.BTC].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.BTC].NETWORK_NAME
  ),
  [Currency.LTC]: createClient(
    CONTRACT_PARAMS[Currency.LTC].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.LTC].NETWORK_NAME
  ),
  [Currency.DOGE]: createClient(
    CONTRACT_PARAMS[Currency.DOGE].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.DOGE].NETWORK_NAME
  ),
  [Currency.EGLD]: createClient(
    CONTRACT_PARAMS[Currency.BTC].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.BTC].NETWORK_NAME
  ),
  [Currency.TRX]: createClient(
    CONTRACT_PARAMS[Currency.TRX].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.TRX].NETWORK_NAME
  ),
  [Currency.USDT_TRX]: createClient(
    CONTRACT_PARAMS[Currency.USDT_TRX].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDT_TRX].NETWORK_NAME
  ),
  [Currency.WBTC_ETH]: createClient(
    CONTRACT_PARAMS[Currency.WBTC_ETH].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.WBTC_ETH].NETWORK_NAME
  ),
  [Currency.WBTC_BNB]: createClient(
    CONTRACT_PARAMS[Currency.WBTC_BNB].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.WBTC_BNB].NETWORK_NAME
  ),
  [Currency.USDC_ETH]: createClient(
    CONTRACT_PARAMS[Currency.USDC_ETH].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDC_ETH].NETWORK_NAME
  ),
  [Currency.USDC_BNB]: createClient(
    CONTRACT_PARAMS[Currency.USDC_BNB].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDC_BNB].NETWORK_NAME
  ),
  [Currency.USD1_ETH]: createClient(
    CONTRACT_PARAMS[Currency.USD1_ETH].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USD1_ETH].NETWORK_NAME
  ),
  [Currency.USD1_BNB]: createClient(
    CONTRACT_PARAMS[Currency.USD1_BNB].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USD1_BNB].NETWORK_NAME
  ),
  [Currency.POL]: createClient(
    CONTRACT_PARAMS[Currency.POL].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.POL].NETWORK_NAME
  ),
  [Currency.USDT_POL]: createClient(
    CONTRACT_PARAMS[Currency.USDT_POL].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDT_POL].NETWORK_NAME
  ),
  [Currency.USDC_POL]: createClient(
    CONTRACT_PARAMS[Currency.USDC_POL].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDC_POL].NETWORK_NAME
  ),
  [Currency.ARB]: createClient(
    CONTRACT_PARAMS[Currency.ARB].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.ARB].NETWORK_NAME
  ),
  [Currency.USDT_ARB]: createClient(
    CONTRACT_PARAMS[Currency.USDT_ARB].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDT_ARB].NETWORK_NAME
  ),
  [Currency.USDC_ARB]: createClient(
    CONTRACT_PARAMS[Currency.USDC_ARB].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDC_ARB].NETWORK_NAME
  ),
  [Currency.ETH_ARB]: createClient(
    CONTRACT_PARAMS[Currency.ETH_ARB].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.ETH_ARB].NETWORK_NAME
  ),
  [Currency.WCWEB_ETH]: createClient(
    CONTRACT_PARAMS[Currency.WCWEB_ETH].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.WCWEB_ETH].NETWORK_NAME
  ),
  [Currency.ETH_BASE]: createClient(
    CONTRACT_PARAMS[Currency.ETH_BASE].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.ETH_BASE].NETWORK_NAME
  ),
  [Currency.USDT_BASE]: createClient(
    CONTRACT_PARAMS[Currency.USDT_BASE].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDT_BASE].NETWORK_NAME
  ),
  [Currency.USDC_BASE]: createClient(
    CONTRACT_PARAMS[Currency.USDC_BASE].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDC_BASE].NETWORK_NAME
  ),
  [Currency.SOL]: createClient(
    CONTRACT_PARAMS[Currency.SOL].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.SOL].NETWORK_NAME
  ),
  [Currency.USDT_SOL]: createClient(
    CONTRACT_PARAMS[Currency.USDT_SOL].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDT_SOL].NETWORK_NAME
  ),
  [Currency.USDC_SOL]: createClient(
    CONTRACT_PARAMS[Currency.USDC_SOL].L2_CONTRACT_ADDRESS_BASE,
    CONTRACT_PARAMS[Currency.USDC_SOL].NETWORK_NAME
  ),
};

export const makerClients: {
  [key in Exclude<Currency, Currency.CWEB>]: ContractClient;
} = {
  [Currency.ETH]: createClient(
    CONTRACT_PARAMS[Currency.ETH].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.ETH].NETWORK_NAME
  ),
  [Currency.BNB]: createClient(
    CONTRACT_PARAMS[Currency.BNB].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.BNB].NETWORK_NAME
  ),
  [Currency.USDT_ETH]: createClient(
    CONTRACT_PARAMS[Currency.USDT_ETH].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDT_ETH].NETWORK_NAME
  ),
  [Currency.USDT_BNB]: createClient(
    CONTRACT_PARAMS[Currency.USDT_BNB].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDT_BNB].NETWORK_NAME
  ),
  [Currency.BTC]: createClient(
    CONTRACT_PARAMS[Currency.BTC].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.BTC].NETWORK_NAME
  ),
  [Currency.LTC]: createClient(
    CONTRACT_PARAMS[Currency.LTC].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.LTC].NETWORK_NAME
  ),
  [Currency.DOGE]: createClient(
    CONTRACT_PARAMS[Currency.DOGE].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.DOGE].NETWORK_NAME
  ),
  [Currency.EGLD]: createClient(
    CONTRACT_PARAMS[Currency.BTC].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.BTC].NETWORK_NAME
  ),
  [Currency.TRX]: createClient(
    CONTRACT_PARAMS[Currency.TRX].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.TRX].NETWORK_NAME
  ),
  [Currency.USDT_TRX]: createClient(
    CONTRACT_PARAMS[Currency.USDT_TRX].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDT_TRX].NETWORK_NAME
  ),
  [Currency.WBTC_ETH]: createClient(
    CONTRACT_PARAMS[Currency.WBTC_ETH].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.WBTC_ETH].NETWORK_NAME
  ),
  [Currency.WBTC_BNB]: createClient(
    CONTRACT_PARAMS[Currency.WBTC_BNB].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.WBTC_BNB].NETWORK_NAME
  ),
  [Currency.USDC_ETH]: createClient(
    CONTRACT_PARAMS[Currency.USDC_ETH].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDC_ETH].NETWORK_NAME
  ),
  [Currency.USDC_BNB]: createClient(
    CONTRACT_PARAMS[Currency.USDC_BNB].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDC_BNB].NETWORK_NAME
  ),
  [Currency.USD1_ETH]: createClient(
    CONTRACT_PARAMS[Currency.USD1_ETH].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USD1_ETH].NETWORK_NAME
  ),
  [Currency.USD1_BNB]: createClient(
    CONTRACT_PARAMS[Currency.USD1_BNB].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USD1_BNB].NETWORK_NAME
  ),
  [Currency.POL]: createClient(
    CONTRACT_PARAMS[Currency.POL].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.POL].NETWORK_NAME
  ),
  [Currency.USDT_POL]: createClient(
    CONTRACT_PARAMS[Currency.USDT_POL].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDT_POL].NETWORK_NAME
  ),
  [Currency.USDC_POL]: createClient(
    CONTRACT_PARAMS[Currency.USDC_POL].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDC_POL].NETWORK_NAME
  ),
  [Currency.ARB]: createClient(
    CONTRACT_PARAMS[Currency.ARB].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.ARB].NETWORK_NAME
  ),
  [Currency.USDT_ARB]: createClient(
    CONTRACT_PARAMS[Currency.USDT_ARB].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDT_ARB].NETWORK_NAME
  ),
  [Currency.USDC_ARB]: createClient(
    CONTRACT_PARAMS[Currency.USDC_ARB].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDC_ARB].NETWORK_NAME
  ),
  [Currency.ETH_ARB]: createClient(
    CONTRACT_PARAMS[Currency.ETH_ARB].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.ETH_ARB].NETWORK_NAME
  ),
  [Currency.WCWEB_ETH]: createClient(
    CONTRACT_PARAMS[Currency.WCWEB_ETH].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.WCWEB_ETH].NETWORK_NAME
  ),
  [Currency.ETH_BASE]: createClient(
    CONTRACT_PARAMS[Currency.ETH_BASE].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.ETH_BASE].NETWORK_NAME
  ),
  [Currency.USDT_BASE]: createClient(
    CONTRACT_PARAMS[Currency.USDT_BASE].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDT_BASE].NETWORK_NAME
  ),
  [Currency.USDC_BASE]: createClient(
    CONTRACT_PARAMS[Currency.USDC_BASE].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDC_BASE].NETWORK_NAME
  ),
  [Currency.SOL]: createClient(
    CONTRACT_PARAMS[Currency.SOL].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.SOL].NETWORK_NAME
  ),
  [Currency.USDT_SOL]: createClient(
    CONTRACT_PARAMS[Currency.USDT_SOL].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDT_SOL].NETWORK_NAME
  ),
  [Currency.USDC_SOL]: createClient(
    CONTRACT_PARAMS[Currency.USDC_SOL].L2_CONTRACT_ADDRESS_MAKER,
    CONTRACT_PARAMS[Currency.USDC_SOL].NETWORK_NAME
  ),
};

export const clients: Record<string, ContractClient> = new Proxy(
  Object.fromEntries(
    [...Object.values(baseClients), ...Object.values(makerClients)].map((client) => [client.contractAddress, client])
  ),
  {
    get(clients, contractId: string) {
      if (!(contractId in clients)) {
        throw new Error(`Client for contractId=${String(contractId)} not found`);
      }

      return clients[contractId];
    },
  }
);

export const mmListClient = createClient(ENV.VITE_VERIFIER_CONTRACT, ENV.VITE_VERIFIER_NETWORK_NAME as NetworkName);
